import { useEffect, useState } from "react";
import { Activity, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

export default function AdminCredit({ goTab }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [minBal, setMinBal] = useState("");
  const [minCov, setMinCov] = useState("");
  const [emailOn, setEmailOn] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/credit").then(({ data }) => {
    setData(data.data);
    setMinBal(String(data.data.thresholds.min_balance_usd));
    setMinCov(String(data.data.thresholds.min_coverage_pct));
    setEmailOn(!!data.data.thresholds.email_enabled);
  }).catch(() => setData(null));

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/credit/thresholds", {
        min_balance_usd: parseFloat(minBal) || 0,
        min_coverage_pct: parseFloat(minCov) || 0,
        email_enabled: emailOn,
      });
      setData(data.data);
      toast.success(t("credit.updated"));
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }

  const card = "rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6";
  const num = "font-display font-extrabold text-2xl sm:text-3xl text-slate-900 mt-1";
  const lbl = "font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400";

  return (
    <div data-testid="admin-credit-page" className="mt-10">
      <h2 className="font-display font-bold text-2xl text-slate-900">{t("credit.title")}</h2>
      <p className="mt-1 text-sm text-slate-500 max-w-2xl">{t("credit.sub")}</p>

      <div className={`mt-6 rounded-2xl px-5 py-3.5 text-sm font-medium ${data.alert_active ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
        data-testid="credit-alert-state">
        {data.alert_active
          ? <>{t("credit.alertActive")}: {data.alert_reasons.join(" · ")}</>
          : `✓ ${t("credit.allOk")}`}
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={card}>
          <div className={lbl}>{t("credit.balance")}</div>
          <div className={num} data-testid="credit-balance">
            {data.airalo_balance_eur != null ? `€ ${data.airalo_balance_eur.toFixed(2)}` : "—"}
            {data.airalo_balance_usd != null && <span className="text-sm text-slate-400"> (≈ ${data.airalo_balance_usd.toFixed(2)})</span>}
          </div>
        </div>
        <div className={card}>
          <div className={lbl}>{t("credit.liability")}</div>
          <div className={num} data-testid="credit-liability">€ {data.wallet_liability_eur.toFixed(2)} <span className="text-sm text-slate-400">(≈ ${data.wallet_liability_usd.toFixed(2)})</span></div>
        </div>
        <div className={card}>
          <div className={lbl}>{t("credit.coverage")}</div>
          <div className={`${num} ${data.coverage_pct != null && data.coverage_pct < data.thresholds.min_coverage_pct ? "text-red-600" : ""}`} data-testid="credit-coverage">
            {data.coverage_pct != null ? `${data.coverage_pct}%` : "—"}
          </div>
        </div>
        <div className={card}>
          <div className={lbl}>{t("credit.failuresCount")}</div>
          <div className={`${num} ${data.credit_failures > 0 ? "text-red-600" : ""}`} data-testid="credit-failures-count">{data.credit_failures}</div>
          {data.credit_failures > 0 && (
            <button data-testid="credit-goto-failures" onClick={() => goTab?.("failures")}
              className="mt-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] hover:underline">
              {t("admin.failures")} →
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <form onSubmit={save} className={card} data-testid="credit-thresholds-form">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">{t("credit.thresholds")}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className={lbl}>{t("credit.minBalance")}</span>
              <input data-testid="credit-min-balance" type="number" min="0" step="1" value={minBal}
                onChange={(e) => setMinBal(e.target.value)}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
            </label>
            <label className="block">
              <span className={lbl}>{t("credit.minCoverage")}</span>
              <input data-testid="credit-min-coverage" type="number" min="0" step="5" value={minCov}
                onChange={(e) => setMinCov(e.target.value)}
                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
            </label>
          </div>
          <label className="mt-4 flex items-center gap-3 cursor-pointer select-none">
            <input data-testid="credit-email-toggle" type="checkbox" checked={emailOn} onChange={(e) => setEmailOn(e.target.checked)}
              className="w-4 h-4 accent-[#FF6B35]" />
            <span className="text-sm font-medium text-slate-700">{t("credit.emailOn")}</span>
          </label>
          <button data-testid="credit-save" type="submit" disabled={busy}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-7 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
            {t("admin.save")}
          </button>
        </form>

        <div className={`${card} flex flex-col justify-between`}>
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{t("credit.recharge")}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              {t("credit.banner")}
            </p>
          </div>
          <a data-testid="credit-airalo-portal" href={data.airalo_portal} target="_blank" rel="noreferrer"
            className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-[#1E293B] text-white px-7 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-slate-700 transition-colors">
            partners.airalo.com <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
